import {
  deriveBriefTruthState,
  derivePostTruthState,
  getLatestPublishAttempt,
} from './studioStatusUtils.js'
import {
  getContentType,
  normalizeIdea,
  normalizePost,
} from './contentTypeUtils.js'
import {
  AUTHORITY_ANGLE_PRESETS,
  normalizeCaptureMetadata,
} from './studioWorkflowUtils.js'

function sortByNewest(items = [], getDate) {
  return [...items].sort(
    (a, b) => new Date(getDate(b) || 0) - new Date(getDate(a) || 0)
  )
}

export function buildNavCounts({
  posts = [],
  ideas = [],
  archivedPosts = [],
  archivedIdeas = [],
  researchBriefs = [],
  reports = [],
  truthSuggestions = [],
  strategicAssets = [],
}) {
  return {
    dashboard: 0,
    create: 0,
    ideas: ideas.length,
    truth: truthSuggestions.filter((item) => item.status === 'pending').length,
    drafts: posts.filter((item) => item.status === 'draft').length,
    queue: posts.filter((item) => item.status === 'scheduled').length,
    delivered: posts.filter((item) => item.status === 'published').length,
    research: researchBriefs.length,
    proof: strategicAssets.filter((item) => item.status !== 'archived').length,
    reports: reports.length,
    archive: archivedPosts.length + archivedIdeas.length,
    settings: 0,
  }
}

export function buildAuthorityReviewState({
  posts = [],
  ideas = [],
  authorityReview = null,
  authorityReviewLoading = false,
}) {
  const pillarCounts = {}
  const angleCounts = {}

  ideas.forEach((idea) => {
    const normalized = normalizeIdea(idea)
    const metadata = normalizeCaptureMetadata(normalized.metadata, {
      createdAt: normalized.createdAt,
    })
    pillarCounts[normalized.pillar] = (pillarCounts[normalized.pillar] || 0) + 1
    if (metadata.authorityAngle) {
      angleCounts[metadata.authorityAngle] =
        (angleCounts[metadata.authorityAngle] || 0) + 1
    }
  })

  posts.forEach((post) => {
    const normalized = normalizePost(post)
    const sourceWork = normalized.metadata?.sourceWork || {}
    pillarCounts[normalized.pillar] = (pillarCounts[normalized.pillar] || 0) + 1
    if (sourceWork.authorityAngle) {
      angleCounts[sourceWork.authorityAngle] =
        (angleCounts[sourceWork.authorityAngle] || 0) + 1
    }
  })

  const missingAngles = AUTHORITY_ANGLE_PRESETS.filter(
    (angle) => !angleCounts[angle.key]
  )
  const strongAngles = AUTHORITY_ANGLE_PRESETS.filter(
    (angle) => angleCounts[angle.key]
  )

  return {
    loading: authorityReviewLoading,
    review: authorityReview,
    persistence: 'session',
    persistenceLabel: 'Session only',
    status: authorityReview?.status || 'local',
    pillarBalance: pillarCounts,
    angleCounts,
    missingAngles,
    strongAngles,
    nextAction:
      authorityReview?.recommendations?.[0] ||
      (missingAngles[0]
        ? `Capture ${missingAngles[0].label.toLowerCase()} evidence`
        : 'Run the next source-fit review before drafting'),
  }
}

export function buildResumeItems({
  posts = [],
  researchBriefs = [],
  publishAttempts = [],
  integrationHealth = null,
}) {
  const drafts = sortByNewest(
    posts.filter((item) => item.status === 'draft'),
    (item) => item.updatedAt || item.createdAt
  )
  const scheduled = [...posts]
    .filter((item) => item.status === 'scheduled' && item.scheduledFor)
    .sort((a, b) => new Date(a.scheduledFor) - new Date(b.scheduledFor))
  const queuedWithTruth = scheduled.map((post) => ({
    post,
    truth: derivePostTruthState({ post, publishAttempts, integrationHealth }),
  }))

  const items = []
  const usedPostIds = new Set()

  const overduePlanned = queuedWithTruth.find(
    (entry) => entry.truth.scheduleState === 'overdue'
  )
  if (overduePlanned) {
    items.push({
      id: `overdue:${overduePlanned.post.id}`,
      kind: 'overdue-planned',
      label: 'Overdue planned item',
      title: overduePlanned.post.topic,
      meta: overduePlanned.post.scheduledFor,
      actionTab: 'queue',
      targetId: overduePlanned.post.id,
    })
    usedPostIds.add(overduePlanned.post.id)
  }

  const dueToday = queuedWithTruth.find(
    (entry) => entry.truth.scheduleState === 'today'
  )
  if (dueToday) {
    items.push({
      id: `due-today:${dueToday.post.id}`,
      kind: 'due-today',
      label: 'Due today',
      title: dueToday.post.topic,
      meta: dueToday.post.scheduledFor,
      actionTab: 'queue',
      targetId: dueToday.post.id,
    })
    usedPostIds.add(dueToday.post.id)
  }

  const failedPublish = posts
    .map((post) => ({
      post,
      truth: derivePostTruthState({ post, publishAttempts, integrationHealth }),
    }))
    .filter((entry) => entry.truth.hasPublishFailure)
    .sort(
      (a, b) =>
        new Date(
          b.truth.lastError?.at || b.post.updatedAt || b.post.createdAt || 0
        ) -
        new Date(
          a.truth.lastError?.at || a.post.updatedAt || a.post.createdAt || 0
        )
    )[0]

  if (failedPublish) {
    items.push({
      id: `publish-failure:${failedPublish.post.id}`,
      kind: 'publish-failure',
      label: 'Publish failed',
      title: failedPublish.post.topic,
      meta: failedPublish.truth.lastError?.code || 'Retry available',
      actionTab:
        failedPublish.post.status === 'published'
          ? 'delivered'
          : failedPublish.post.status === 'scheduled'
            ? 'queue'
            : 'drafts',
      targetId: failedPublish.post.id,
    })
    usedPostIds.add(failedPublish.post.id)
  }

  const failedSync = posts
    .map((post) => ({
      post,
      truth: derivePostTruthState({ post, publishAttempts, integrationHealth }),
    }))
    .filter((entry) => entry.truth.hasSyncFailure)
    .sort(
      (a, b) =>
        new Date(
          b.truth.lastError?.at || b.post.updatedAt || b.post.createdAt || 0
        ) -
        new Date(
          a.truth.lastError?.at || a.post.updatedAt || a.post.createdAt || 0
        )
    )[0]

  if (failedSync) {
    items.push({
      id: `sync-failure:${failedSync.post.id}`,
      kind: 'sync-failure',
      label: 'Sync failed',
      title: failedSync.post.topic,
      meta: failedSync.truth.lastError?.code || 'Retry sync',
      actionTab: failedSync.post.status === 'scheduled' ? 'queue' : 'drafts',
      targetId: failedSync.post.id,
    })
    usedPostIds.add(failedSync.post.id)
  }

  const checklistIncomplete = queuedWithTruth.find(
    (entry) =>
      entry.truth.hasIncompleteChecklist && !usedPostIds.has(entry.post.id)
  )
  if (checklistIncomplete) {
    items.push({
      id: `checklist:${checklistIncomplete.post.id}`,
      kind: 'checklist-incomplete',
      label: 'Checklist incomplete',
      title: checklistIncomplete.post.topic,
      meta: getContentType(normalizePost(checklistIncomplete.post).contentType)
        .label,
      actionTab: 'queue',
      targetId: checklistIncomplete.post.id,
    })
    usedPostIds.add(checklistIncomplete.post.id)
  }

  const deliveredManually = sortByNewest(
    posts
      .map((post) => ({
        post,
        truth: derivePostTruthState({
          post,
          publishAttempts,
          integrationHealth,
        }),
      }))
      .filter((entry) => entry.truth.deliveredManually),
    (entry) =>
      entry.truth.deliveryReceipt?.deliveredAt ||
      entry.post.publishedAt ||
      entry.post.updatedAt
  )[0]

  if (deliveredManually) {
    items.push({
      id: `manual-delivery:${deliveredManually.post.id}`,
      kind: 'delivered-manually',
      label: 'Delivered manually',
      title: deliveredManually.post.topic,
      meta: deliveredManually.truth.deliveryReceipt?.externalUrlOrRef || '',
      actionTab: 'delivered',
      targetId: deliveredManually.post.id,
    })
    usedPostIds.add(deliveredManually.post.id)
  }

  const feedbackNeeded = sortByNewest(
    posts
      .map((post) => ({
        post,
        truth: derivePostTruthState({
          post,
          publishAttempts,
          integrationHealth,
        }),
      }))
      .filter(
        (entry) =>
          entry.post.status === 'published' &&
          !entry.truth.hasPerformanceFeedback
      ),
    (entry) =>
      entry.truth.deliveryReceipt?.deliveredAt ||
      entry.post.publishedAt ||
      entry.post.updatedAt ||
      entry.post.createdAt
  )[0]

  if (feedbackNeeded) {
    items.push({
      id: `feedback-needed:${feedbackNeeded.post.id}`,
      kind: 'feedback-needed',
      label: 'Feedback needed',
      title: feedbackNeeded.post.topic,
      meta: 'Add first check-in',
      actionTab: 'delivered',
      targetId: feedbackNeeded.post.id,
    })
    usedPostIds.add(feedbackNeeded.post.id)
  }

  const convertedCaptureDraft = drafts.find((draft) => {
    const sourceWork = draft.metadata?.sourceWork
    return (
      (draft.createdFromIdeaId || sourceWork?.ideaId) &&
      !usedPostIds.has(draft.id)
    )
  })
  if (convertedCaptureDraft) {
    items.push({
      id: `converted-capture:${convertedCaptureDraft.id}`,
      kind: 'converted-capture',
      label: 'Converted capture ready for follow-up',
      title: convertedCaptureDraft.topic,
      meta: getContentType(normalizePost(convertedCaptureDraft).contentType)
        .label,
      actionTab: 'drafts',
      targetId: convertedCaptureDraft.id,
    })
    usedPostIds.add(convertedCaptureDraft.id)
  }

  const latestDraft = drafts.find((draft) => !usedPostIds.has(draft.id))
  if (latestDraft) {
    items.push({
      id: `draft:${latestDraft.id}`,
      kind: 'draft',
      label: 'Recent draft',
      title: latestDraft.topic,
      meta: getContentType(normalizePost(latestDraft).contentType).label,
      actionTab: 'drafts',
      targetId: latestDraft.id,
    })
    usedPostIds.add(latestDraft.id)
  }

  const nextScheduled = queuedWithTruth.find(
    (entry) =>
      entry.truth.scheduleState === 'future' && !usedPostIds.has(entry.post.id)
  )?.post
  if (nextScheduled) {
    items.push({
      id: `scheduled:${nextScheduled.id}`,
      kind: 'scheduled',
      label: 'Next planned item',
      title: nextScheduled.topic,
      meta: nextScheduled.scheduledFor,
      actionTab: 'queue',
      targetId: nextScheduled.id,
    })
  }

  const unfinishedBrief = sortByNewest(
    researchBriefs.filter((brief) => {
      const truth = deriveBriefTruthState({ brief, integrationHealth })
      return truth.hasSyncFailure || !brief.notionBriefId
    }),
    (brief) => brief.updatedAt || brief.generatedAt
  )[0]

  if (unfinishedBrief) {
    items.push({
      id: `brief:${unfinishedBrief.id}`,
      kind: 'brief',
      label: 'Research in progress',
      title: unfinishedBrief.topic,
      meta: unfinishedBrief.notionBriefId ? 'Saved brief' : 'Needs sync review',
      actionTab: 'research',
      targetId: unfinishedBrief.id,
    })
  }

  return items.slice(0, 5)
}

export function buildDashboardState({
  posts = [],
  ideas = [],
  projects = [],
  positions = [],
  archivedPosts = [],
  archivedIdeas = [],
  researchBriefs = [],
  reports = [],
  publishAttempts = [],
  runtimeVerifications = [],
  integrationHealth = null,
  authorityReview = null,
  authorityReviewLoading = false,
  authorityTopicCandidates = [],
  strategicAssets = [],
  strategicAssetCoverage = null,
}) {
  const drafts = posts.filter((item) => item.status === 'draft')
  const queue = posts.filter((item) => item.status === 'scheduled')
  const delivered = posts.filter((item) => item.status === 'published')
  const syncFailed = posts.filter(
    (post) =>
      derivePostTruthState({ post, publishAttempts, integrationHealth })
        .hasSyncFailure
  )
  const publishFailed = posts.filter(
    (post) =>
      derivePostTruthState({ post, publishAttempts, integrationHealth })
        .hasPublishFailure
  )
  const synced = posts.filter((post) => !!post.notionPageId)
  const nextPlannedItem =
    [...queue]
      .filter((item) => item.scheduledFor)
      .sort((a, b) => new Date(a.scheduledFor) - new Date(b.scheduledFor))[0] ||
    null
  const latestAttempt = publishAttempts[0] || null
  const recentDrafts = sortByNewest(
    drafts,
    (item) => item.updatedAt || item.createdAt
  ).slice(0, 6)
  const recentIdeas = sortByNewest(
    ideas,
    (item) => item.updatedAt || item.createdAt
  ).slice(0, 6)
  const activeResearch = sortByNewest(
    researchBriefs.filter(
      (brief) =>
        !brief.notionBriefId ||
        deriveBriefTruthState({ brief, integrationHealth }).hasSyncFailure
    ),
    (brief) => brief.updatedAt || brief.generatedAt
  ).slice(0, 4)
  const recentReports = sortByNewest(reports, (item) => item.createdAt).slice(
    0,
    4
  )
  const activeProofAssets = strategicAssets.filter(
    (asset) => asset.status !== 'archived'
  )
  const activeProjects = projects.filter((project) => !project.archivedAt)
  const activePositions = positions.filter((position) => !position.archivedAt)
  const unlinkedCaptures = ideas.filter(
    (idea) => !idea.projectIds?.length && !idea.positionIds?.length
  )
  const pendingTopicCandidates = authorityTopicCandidates.filter(
    (candidate) =>
      candidate.status === 'pending' || candidate.status === 'accepted'
  )
  const recentProofAssets = sortByNewest(
    activeProofAssets,
    (item) => item.updatedAt || item.createdAt
  ).slice(0, 4)
  const recentVerifications = sortByNewest(
    runtimeVerifications,
    (item) => item.finishedAt || item.startedAt || item.createdAt
  ).slice(0, 4)

  return {
    counts: {
      drafts: drafts.length,
      queue: queue.length,
      delivered: delivered.length,
      syncFailed: syncFailed.length,
      publishFailed: publishFailed.length,
      ideas: ideas.length,
      synced: synced.length,
      reports: reports.length,
      proofAssets: activeProofAssets.length,
      strongProofAssets:
        strategicAssetCoverage?.strong ||
        activeProofAssets.filter((asset) => asset.proofStrength === 'strong')
          .length,
      unlinkedProofAssets:
        strategicAssetCoverage?.unlinked ||
        activeProofAssets.filter(
          (asset) => !asset.projectIds?.length && !asset.positionIds?.length
        ).length,
      unusedProofAssets:
        strategicAssetCoverage?.unused ||
        activeProofAssets.filter((asset) => !asset.usageCount).length,
      archive: archivedPosts.length + archivedIdeas.length,
    },
    bodyOfWorkHealth: [
      {
        key: 'active-projects',
        label: 'Active projects',
        value: activeProjects.length,
        tone: activeProjects.length ? 'success' : 'warning',
        tab: 'truth',
      },
      {
        key: 'positions',
        label: 'Canonical positions',
        value: activePositions.length,
        tone: activePositions.length ? 'success' : 'warning',
        tab: 'truth',
      },
      {
        key: 'strong-proof',
        label: 'Strong proof assets',
        value:
          strategicAssetCoverage?.strong ||
          activeProofAssets.filter((asset) => asset.proofStrength === 'strong')
            .length,
        tone:
          (strategicAssetCoverage?.strong ||
            activeProofAssets.filter(
              (asset) => asset.proofStrength === 'strong'
            ).length) > 0
            ? 'success'
            : 'warning',
        tab: 'proof',
      },
      {
        key: 'unlinked-captures',
        label: 'Unlinked captures',
        value: unlinkedCaptures.length,
        tone: unlinkedCaptures.length ? 'warning' : 'success',
        tab: 'ideas',
      },
      {
        key: 'pending-topics',
        label: 'Pending topics',
        value: pendingTopicCandidates.length,
        tone: pendingTopicCandidates.length ? 'planning' : 'muted',
        tab: 'ideas',
      },
      {
        key: 'unused-proof',
        label: 'Unused proof',
        value:
          strategicAssetCoverage?.unused ||
          activeProofAssets.filter((asset) => !asset.usageCount).length,
        tone:
          (strategicAssetCoverage?.unused ||
            activeProofAssets.filter((asset) => !asset.usageCount).length) > 0
            ? 'warning'
            : 'success',
        tab: 'proof',
      },
    ],
    nextPlannedItem,
    latestAttempt,
    recentDrafts,
    recentIdeas,
    activeResearch,
    recentReports,
    recentProofAssets,
    recentVerifications,
    authority: buildAuthorityReviewState({
      posts,
      ideas,
      authorityReview,
      authorityReviewLoading,
    }),
    resumeItems: buildResumeItems({
      posts,
      researchBriefs,
      publishAttempts,
      integrationHealth,
    }),
  }
}

export function filterArchiveItems(
  items = [],
  { query = '', pillar = 'all', contentType = 'all' } = {}
) {
  const lower = query.trim().toLowerCase()
  return items.filter((item) => {
    const normalized = item.text ? normalizeIdea(item) : normalizePost(item)
    const matchesQuery =
      !lower ||
      String(normalized.topic || normalized.text || '')
        .toLowerCase()
        .includes(lower) ||
      String(normalized.content || '')
        .toLowerCase()
        .includes(lower)
    const matchesPillar = pillar === 'all' || normalized.pillar === pillar
    const matchesType =
      contentType === 'all' || normalized.contentType === contentType
    return matchesQuery && matchesPillar && matchesType
  })
}

export default {
  buildNavCounts,
  buildAuthorityReviewState,
  buildResumeItems,
  buildDashboardState,
  filterArchiveItems,
  getLatestPublishAttempt,
}
